import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { useApiClient } from '@/api/useApiClient'
import type { ServiceType } from '@/constants/environments'
import { useToast } from '@/composables/useToast'

/*
 * Once `npm run generate:api-all` has been run:
 * - Replace `any` with `components['schemas']['SchoolColorTheme']` /
 *   `components['schemas']['SchoolColorThemeData']` from api-types.ts.
 */

export interface SchoolColorThemeData {
  name: string
  bulletin_badge: string
  card_background: string
  table_time_background: string
  table_background: string
  table_header_color: string
  primary_header: string
  table_day_cell: string
  table_white_stripes: string
  table_dark_stripes: string
  default_btn_hover: string
  sidebar_bg: string
  icon_color: string
  sidebar_hover_bg: string
  sidebar_hover_color: string
  logout_hover_bg: string
  table_row_hover: string
  progress_bg: string
  progress_color: string
  default_btn: string
  // Optional
  ghost_btn_hover?: string
  ghost_btn_color?: string
  hover_input_border?: string
  statement_bg?: string
  statement_border?: string
  statement_color?: string
}

export interface SchoolColorTheme extends SchoolColorThemeData {
  id: string
  is_default: boolean
  is_custom: boolean
}

export function useColorThemes(service: ServiceType) {
  const { GET } = useApiClient(service)

  return useQuery({
    queryKey: ['color-themes', service],
    queryFn: async () => {
      const { data, error } = await GET('/admin/school-color-themes')
      if (error) {
        throw new Error(
          typeof error === 'string' ? error : 'Failed to fetch color themes',
        )
      }
      // A 200 with an empty body yields undefined — normalize to [].
      return (data ?? []) as SchoolColorTheme[]
    },
  })
}

export function useCreateColorTheme(service: ServiceType) {
  const { POST } = useApiClient(service)
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: async (body: SchoolColorThemeData) => {
      const { data, error } = await POST('/admin/school-color-themes', { body })
      if (error) {
        throw new Error(
          typeof error === 'string' ? error : 'Failed to create color theme',
        )
      }
      return data as SchoolColorTheme
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['color-themes', service] })
      toast.success('Color theme created successfully')
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to create color theme')
    },
  })
}

export function useUpdateColorTheme(service: ServiceType) {
  const { PATCH } = useApiClient(service)
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string
      body: SchoolColorThemeData
    }) => {
      const { data, error } = await PATCH('/admin/school-color-themes/{id}', {
        params: { path: { id } },
        body,
      })
      if (error) {
        throw new Error(
          typeof error === 'string' ? error : 'Failed to update color theme',
        )
      }
      return data as SchoolColorTheme
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['color-themes', service] })
      toast.success('Color theme updated successfully')
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to update color theme')
    },
  })
}

/**
 * Assign an existing library theme to a specific school BY REFERENCE
 * (sets schools.color_theme_id — no copy of colors).
 * Backend: PATCH /admin/schools/{id}/color-theme  body {color_theme_id}.
 */
export function useAssignSchoolColorTheme(service: ServiceType) {
  const { PATCH } = useApiClient(service)
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: async ({
      schoolId,
      colorThemeId,
    }: {
      schoolId: string
      colorThemeId: string | null
    }) => {
      const { data, error } = await PATCH('/admin/schools/{id}/color-theme', {
        params: { path: { id: schoolId } },
        body: { color_theme_id: colorThemeId },
      })
      if (error) {
        throw new Error(
          typeof error === 'string' ? error : 'Failed to assign theme',
        )
      }
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schools', service] })
      toast.success('Theme assigned to school')
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to assign theme')
    },
  })
}
